import React from 'react';
import { RefreshCw, CheckCircle2, Clock, AlertTriangle, ShieldCheck } from 'lucide-react';
import { AlertResponseMetadata } from '../../types/alert';
import { formatRelativeTime } from '../../utils/formatters';

interface FreshnessIndicatorProps {
  metadata?: AlertResponseMetadata | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const FreshnessIndicator: React.FC<FreshnessIndicatorProps> = ({
  metadata,
  onRefresh,
  isRefreshing = false,
}) => {
  if (!metadata) return null;

  const isStale = metadata.isFallback || (metadata.cacheAgeSeconds && metadata.cacheAgeSeconds > 300);

  return (
    <aside
      aria-label="Data Freshness and Ingestion Status"
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 bg-white border border-slate-200/90 rounded-xl shadow-2xs text-xs font-sans text-navy-900"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        {isStale ? (
          <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md font-mono text-[11px] font-bold border border-amber-200">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <span>STALE CACHED SNAPSHOT</span>
          </span>
        ) : metadata.isCached ? (
          <span className="inline-flex items-center gap-1.5 text-blue-800 bg-blue-50/80 px-2 py-0.5 rounded-md font-mono text-[11px] font-bold border border-blue-200/80">
            <Clock className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
            <span>EDGE CACHED ({metadata.cacheAgeSeconds}S AGO)</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-md font-mono text-[11px] font-bold border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
            <span>VERIFIED NWS LIVE STREAM</span>
          </span>
        )}

        <span className="text-slate-300 hidden sm:inline">•</span>
        <span className="text-slate-500 font-mono text-[11px] hidden sm:inline">
          Sync: {formatRelativeTime(metadata.lastChecked)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200/80 border border-slate-200 rounded-lg transition-colors disabled:opacity-50"
            title="Poll National Weather Service for updated bulletins"
            aria-label="Refresh alert data from official source"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-blue-600' : 'text-slate-500'}`} />
            <span>{isRefreshing ? 'Polling NWS...' : 'Poll Source'}</span>
          </button>
        )}
      </div>
    </aside>
  );
};
